import { useReactiveVar } from '@apollo/client'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback } from 'react'
import { useDragAndDropActions, useIsDragging } from '../DragAndDropStore'
import {
    useHoveredItemActions,
    useSelectedMainMenuNode,
} from '../HoveredItemStore'
import { useSelectedKey } from '../NodeKeySelectionStore'
import { useShallowSelector } from '../Workspace2Store'
import { useHoveredActionMutationService } from '../services/hoveredActionMutationService'
import { useTransactionManager } from '../services/transactionManager'
import { ContentItemViewNodesType } from '../types'
import { DragAndDropInfo } from '../types/dragDropInfo.type'
import { DataNode } from '../types/types'
import {
    getDragAndDropNodes,
    getSortedSiblings,
    reorderSiblingContents,
    replaceNodesById,
} from '../utils'
import { tryGetShortcutKey } from '../utils/shortcutKeyUtils'

export const useReorderItems = () => {
    const { setSelectedMainMenuNode } = useHoveredItemActions()
    const selectedKey = useSelectedKey()
    const {
        contentItemViewNodes,
        actions: { setContentItemViewNodes },
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        articlesLoading: state.articlesLoading,
        actions: state.actions,
    }))
    const {
        mutateReorderFamilyTemplatesAsync,
        mutateReorderProductTypeTemplatesAsync,
    } = useHoveredActionMutationService()
    const { executeWithOptimistic } = useTransactionManager()
    const selectedMainMenuNode = useSelectedMainMenuNode()
    const { setIsMoveBlockMenuDisplayed } = useHoveredItemActions()
    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )
    const isDragging = useIsDragging()
    const { setIsDragging } = useDragAndDropActions()
    const handleDragStart = useCallback(() => setIsDragging(true), [
        setIsDragging,
    ])
    const handleDragEnd = useCallback(() => setIsDragging(false), [
        setIsDragging,
    ])

    const runReorderMutation = useCallback(
        (
            reorderedSiblings: DataNode[],
            apiPayload: any,
            fallbackState: string
        ) => {
            executeWithOptimistic({
                fallbackState,
                asyncAction: async () => {
                    if (
                        selectedProjectElementNode?.elementType ===
                        'PRODUCTFAMILY'
                    ) {
                        mutateReorderFamilyTemplatesAsync?.(apiPayload)
                    } else {
                        mutateReorderProductTypeTemplatesAsync?.(apiPayload)
                    }
                    return reorderedSiblings
                },
                successCbFn: () => {
                    const updatedNodes = replaceNodesById(
                        contentItemViewNodes,
                        reorderedSiblings
                    )
                    setContentItemViewNodes(updatedNodes)
                },
                errorCbFn: () => setContentItemViewNodes(contentItemViewNodes),
            })
        },
        [
            executeWithOptimistic,
            selectedProjectElementNode,
            mutateReorderFamilyTemplatesAsync,
            mutateReorderProductTypeTemplatesAsync,
            contentItemViewNodes,
            setContentItemViewNodes,
        ]
    )

    /**
     * Handles the drop event for tree nodes.
     * This function manages the reordering of nodes within the same parent hierarchy
     * and ensures that the drag-and-drop operation adheres to the defined constraints.
     *
     * @param {Object} info - The information about the drop event.
     */
    const handleOnDrop = useCallback(
        (info: DragAndDropInfo) => {
            const {
                dragKey,
                dropKey,
                dragNode,
                dropNode,
            } = getDragAndDropNodes(info, contentItemViewNodes)

            if (!dragNode || !dropNode) {
                return
            }

            // Ensure that the dragged and drop nodes belong to the same parent.
            const differentParent =
                dragNode.dtvParentId !== dropNode.dtvParentId
            const differentContentParent =
                dragNode.dtvContentItemParentId !==
                dropNode.dtvContentItemParentId
            if (differentParent && differentContentParent) return

            // Ensure that the dragged and drop nodes have the same text type.
            if (dragNode.textType !== dropNode.textType) {
                return
            }

            // Get the siblings of the dragged node within the same parent.
            const siblings = getSortedSiblings(
                contentItemViewNodes,
                dragNode.dtvParentId,
                dragNode.dtvContentItemParentId
            )

            // Find the indices of the dragged and drop nodes in the siblings array.
            const dragIndex = siblings.findIndex((node) => node.id === dragKey)
            const dropIndex = siblings.findIndex((node) => node.id === dropKey)

            if (dragIndex === -1 || dropIndex === -1) return

            const { reorderedSiblings, apiPayload } = reorderSiblingContents(
                siblings,
                dragIndex,
                dropIndex,
                dragNode,
                selectedProjectElementNode
            )

            const optimisticState = contentItemViewNodes?.map((node) => ({
                ...node,
                key: node.id || '',
                contentItemNodes: [],
            })) as DataNode<Partial<TextContentElement>>[]

            runReorderMutation(
                reorderedSiblings,
                apiPayload,
                JSON.stringify(optimisticState)
            )
        },
        [
            contentItemViewNodes,
            getSortedSiblings,
            executeWithOptimistic,
            setContentItemViewNodes,
            selectedProjectElementNode,
        ]
    )

    /**
     * Handles the drag-over event for tree nodes.
     * This function ensures that certain drag-and-drop operations are restricted
     * based on the type and hierarchy of the nodes involved.
     *
     * @param {DragAndDropInfo} info - The information about the drag-over event.
     */
    const handleOnDragOver = useCallback(
        (info: DragAndDropInfo) => {
            const { dragNode, dropNode, event } = getDragAndDropNodes(
                info,
                contentItemViewNodes
            )

            if (!dragNode || !dropNode) {
                return
            }

            // Prevent dropping an article node onto its own children.
            // This ensures that an article cannot be dropped into its descendants, maintaining a valid hierarchy.
            if (
                dragNode.type === NODE_TYPES.Article &&
                !dragNode.dtvParentId &&
                dropNode.dtvParentId === dragNode.id
            ) {
                event?.preventDefault()
                return
            }

            // Prevent dropping nodes outside of their current level.
            // This ensures that nodes remain within the same parent hierarchy.
            if (
                dragNode.type !== NODE_TYPES.Article &&
                dragNode.dtvParentId !== dropNode.dtvParentId
            ) {
                event?.preventDefault()
            }
        },
        [contentItemViewNodes]
    )

    /**
     * Handles moving a block (node) up or down within its parent hierarchy.
     * This function reorders the siblings of the hovered node based on the specified direction.
     * @param {'up' | 'down'} direction - The direction to move the block ('up' or 'down').
     */
    const handleMoveBlock = useCallback(
        (
            direction: 'up' | 'down',
            nodeArg?: Partial<ContentItemViewNodesType>
        ) => {
            const nodeToMove = nodeArg ?? selectedMainMenuNode
            if (!nodeToMove) return

            const sortedSiblings = getSortedSiblings(
                contentItemViewNodes,
                nodeToMove.dtvParentId,
                nodeToMove.dtvContentItemParentId
            )

            const hoveredIndex = sortedSiblings.findIndex(
                (item) => item.id === nodeToMove.id
            )

            if (hoveredIndex === -1) return

            const dragIndex = hoveredIndex
            const dropIndex =
                direction === 'up' ? hoveredIndex - 1 : hoveredIndex + 1

            // Prevent out-of-bounds access
            if (dropIndex < 0 || dropIndex >= sortedSiblings.length) return

            const { reorderedSiblings, apiPayload } = reorderSiblingContents(
                sortedSiblings,
                dragIndex,
                dropIndex,
                nodeToMove,
                selectedProjectElementNode
            )

            // Avoid unnecessary updates if the order hasn't changed
            if (
                JSON.stringify(reorderedSiblings) ===
                JSON.stringify(contentItemViewNodes)
            ) {
                return
            }

            runReorderMutation(
                reorderedSiblings,
                apiPayload,
                JSON.stringify(contentItemViewNodes)
            )
            setIsMoveBlockMenuDisplayed(false)
        },
        [
            contentItemViewNodes,
            selectedMainMenuNode,
            getSortedSiblings,
            executeWithOptimistic,
            setContentItemViewNodes,
            selectedProjectElementNode,
            setIsMoveBlockMenuDisplayed,
        ]
    )

    const getReorderDirection = (
        e: KeyboardEvent | React.KeyboardEvent
    ): 'up' | 'down' | null => {
        if (tryGetShortcutKey('MoveUp', e)) return 'up'
        if (tryGetShortcutKey('MoveDown', e)) return 'down'

        return null
    }

    /**
     * Handles keyboard shortcuts for reordering items.
     *
     * @param {KeyboardEvent | React.KeyboardEvent} e - The keyboard event.
     */
    const handleKeyDown = useCallback(
        (e: KeyboardEvent | React.KeyboardEvent) => {
            const direction = getReorderDirection(e)
            if (!direction || !selectedKey) return

            e.preventDefault()

            const node = contentItemViewNodes.find((n) => n.id === selectedKey)
            if (!node || node.isHidden) return

            handleMoveBlock(direction, node)
        },
        [selectedKey, contentItemViewNodes, handleMoveBlock]
    )

    /**
     * Handles keydown events.
     * - Triggers block reorder shortcuts (MoveUp / MoveDown) even in editable fields.
     * - Allows normal typing otherwise.
     *
     * @param {React.KeyboardEvent} e - The keyboard event.
     */
    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (getReorderDirection(e)) {
                handleKeyDown(e)
            }
        },
        [handleKeyDown]
    )

    return {
        handleOnDrop,
        handleOnDragOver,
        handleMoveBlock,
        isDragging,
        handleDragStart,
        handleDragEnd,
        handleKeyDown,
        onKeyDown,
    }
}
